import { initializer, inject, injectable } from "@launchtray/tsyringe-async";
import { ICanvasScaler } from "by/gamefactory/canvas/ICanvasScaler";
import { PromiseCompletionSource } from "by/gamefactory/shared/async/PromiseCompletionSource";
import { Viewable } from "by/gamefactory/scene/viewable/Viewable";
import { BuildingRankUpPopupView } from "./view/BuildingRankUpPopupView";
import { ILogger } from "by/gamefactory/cityversetycoon/logger/ILogger";
import { BuildingRankUpResourcePackage } from "./resources/BuildingRankUpResourcePackage";
import { AsyncOperationNotification } from "../../common/components/AsyncOperationNotification";
import { CommonResourcesPackage } from "../../common/resources/CommonResourcesPackage";
import { IGuestModel } from "../../common/models/IGuestModel";
import { IPlayerModel } from "../../common/models/IPlayerModel";
import { IBuildingRankUpModel } from "./model/IBuildingRankUpModel";
import { IBuildingRankUpController } from "./controller/IBuildingRankUpController";
import { DynamicResourcesPackage } from "../../../../resources/DynamicResourcesPackage";
import { BuildingRightsType } from "../../../networking/data/responses/user/BuildingRightsType";
import { Texture } from "pixi.js";
import { IBuildingModel } from "../../common/models/Building/IBuildingModel";
import { UserProfileResourcePackage } from "../userprofile/resources/UserProfileResourcePackage";
import { NoCacheDynamicPackage } from "../../common/resources/NoCacheDynamicPackage";
import { TopPanel } from "../../game/components/toppanel/TopPanel";

@injectable()
export class BuildingRankUpPopupScene extends Viewable<void> {
    private readonly canvasScaler: ICanvasScaler;
    private readonly logger: ILogger;
    private readonly asyncOperationNotification: AsyncOperationNotification;
    private readonly commonResources: CommonResourcesPackage;
    private readonly userProfileResources: UserProfileResourcePackage;
    private readonly guestModel: IGuestModel;
    private readonly playerModel: IPlayerModel;
    private readonly buildingRankUpModel: IBuildingRankUpModel;
    private readonly buildingRankUpController: IBuildingRankUpController;
    private readonly buildingModel: IBuildingModel;
    private readonly topPanel: TopPanel;
    private landlordDynamicResources: DynamicResourcesPackage;

    public constructor(
        @inject("ICanvasScaler") canvasScaler: ICanvasScaler,
        @inject("ILogger") logger: ILogger,
        asyncOperationNotification: AsyncOperationNotification,
        commonResources: CommonResourcesPackage,
        userProfileResources: UserProfileResourcePackage,
        @inject("IGuestModel") guestModel: IGuestModel,
        @inject("IPlayerModel") playerModel: IPlayerModel,
        @inject("IBuildingRankUpModel") buildingRankUpModel: IBuildingRankUpModel,
        @inject("IBuildingRankUpController") buildingRankUpController: IBuildingRankUpController,
        @inject("IBuildingModel") buildingModel: IBuildingModel,
        topPanel: TopPanel
    ) {
        super();
        this.canvasScaler = canvasScaler;
        this.logger = logger;
        this.asyncOperationNotification = asyncOperationNotification;
        this.commonResources = commonResources;
        this.userProfileResources = userProfileResources;
        this.guestModel = guestModel;
        this.playerModel = playerModel;
        this.buildingRankUpModel = buildingRankUpModel;
        this.buildingRankUpController = buildingRankUpController;
        this.buildingModel = buildingModel;
        this.topPanel = topPanel;
    }

    @initializer()
    public async initialization(): Promise<void> {
        this.asyncOperationNotification.show();
        try {
            let landlordAvatar = Texture.EMPTY;
            if (this.playerModel.buildingRights.value === BuildingRightsType.RENTER) {
                this.landlordDynamicResources = new NoCacheDynamicPackage("rank_up");
                const avatarUrl = this.buildingModel.landlordData.value.avatarUrl;
                this.landlordDynamicResources.add(avatarUrl);
                await this.landlordDynamicResources.load();
                landlordAvatar = this.landlordDynamicResources.resource(avatarUrl);
            }
            const buildingRankUpResources = new BuildingRankUpResourcePackage();
            await buildingRankUpResources.load();
            this.asyncOperationNotification.hide();
            const viewPCS = new PromiseCompletionSource<void>();
            this.view = new BuildingRankUpPopupView(
                viewPCS,
                this.canvasScaler,
                this.commonResources,
                buildingRankUpResources,
                this.userProfileResources,
                this.guestModel,
                this.playerModel,
                this.buildingRankUpModel,
                this.buildingRankUpController,
                landlordAvatar
            );
            const topPanelOnNotification = this.topPanel.onNotificationLayer;
            this.topPanel.bringToOriginParent(true);
            viewPCS.result.then(() => {
                buildingRankUpResources.destroy();
                this.landlordDynamicResources?.destroy();
                this.userProfileResources.destroy();
                this.operationControls.complete();
                if (topPanelOnNotification) {
                    this.topPanel.bringToFront();
                }
            }, (reason: any) => {
                buildingRankUpResources.destroy();
                this.landlordDynamicResources?.destroy();
                this.userProfileResources.destroy();
                if (topPanelOnNotification) {
                    this.topPanel.bringToFront();
                }
                this.operationControls.error(reason);
            });
        }
        catch (e: unknown) {
            this.logger.error(e);
            this.asyncOperationNotification.hide();
            this.operationControls.complete();
        }
    }
}
